import React from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { LogOut, LayoutDashboard, Calendar, Users, Package, Settings, FileText, Bell, BarChart3, UserCircle2 } from "lucide-react";
import Logo from "./Logo";

const navItemsAdmin = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/admin/schedule", label: "Schedule", icon: Calendar },
  { to: "/admin/clients", label: "Clients", icon: Users },
  { to: "/admin/packages", label: "Packages", icon: Package },
  { to: "/admin/refills", label: "Refills", icon: Bell },
  { to: "/admin/services", label: "Services", icon: Settings },
  { to: "/admin/practitioners", label: "Practitioners", icon: UserCircle2 },
  { to: "/admin/waivers", label: "Waivers", icon: FileText },
  { to: "/admin/reports", label: "Reports", icon: BarChart3 },
];

const navItemsPractitioner = [
  { to: "/practitioner", label: "My Day", icon: Calendar, end: true },
  { to: "/admin/clients", label: "Clients", icon: Users },
];

export default function AdminLayout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const items = user?.role === "practitioner" ? navItemsPractitioner : navItemsAdmin;

  const handleLogout = async () => {
    await logout();
    nav("/admin/login");
  };

  return (
    <div className="min-h-screen bg-appbg flex flex-col">
      {/* Top nav */}
      <header className="border-b border-hairline bg-white">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to={user?.role === "practitioner" ? "/practitioner" : "/admin"} className="flex items-center group" data-testid="admin-logo">
            <Logo size="md" theme="light" />
          </Link>
          <nav className="hidden lg:flex items-center gap-1">
            {items.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-md text-sm font-ui transition-colors duration-150 ${
                    isActive ? "text-forest bg-cream" : "text-slate hover:text-forest hover:bg-sand"
                  }`
                }
                data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {it.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex flex-col items-end leading-tight">
              <span className="text-xs text-slate font-ui">{user?.role}</span>
              <span className="text-sm text-charcoal font-medium font-ui" data-testid="current-user-name">{user?.name}</span>
            </div>
            <button
              onClick={handleLogout}
              className="p-2 rounded-md text-slate hover:text-forest hover:bg-cream transition-colors"
              data-testid="logout-button"
              aria-label="Sign out"
            >
              <LogOut className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>
        {/* Mobile nav */}
        <div className="lg:hidden border-t border-hairline overflow-x-auto">
          <div className="flex items-center gap-1 px-4 py-2 min-w-max">
            {items.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                className={({ isActive }) =>
                  `px-3 py-1.5 rounded-md text-xs font-ui whitespace-nowrap ${
                    isActive ? "text-forest bg-cream" : "text-slate"
                  }`
                }
              >
                {it.label}
              </NavLink>
            ))}
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-[1280px] w-full mx-auto px-6 lg:px-8 py-8">{children}</main>
    </div>
  );
}
